
import React from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, useWindowDimensions } from 'react-native';
import { Typography } from './ui';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../context/ThemeContext';
import { BrandLogo } from './BrandLogo';
import { useI18n } from '../i18n';
import { fontWeights } from '../styles/typographyTokens';

import { ACCENT_TEXT } from '../styles/accentText';
export const CommunityJoinBlock: React.FC = () => {
    const { width } = useWindowDimensions();
    const isMedium = width >= 768;
    const { isDark, colors: themeColors } = useTheme();
    const { t } = useI18n('home');

    return (
        <View style={[styles.wrapper, { backgroundColor: themeColors.canvas }]}>
            <View style={styles.container}>
                <View style={[styles.content, { backgroundColor: themeColors.canvas }, isMedium && styles.contentMedium]}>
                    {/* LEFT COLUMN: TEXT */}
                    <View style={[styles.textColumn, isMedium && styles.textColumnMedium]}>
                        <View style={styles.badgeContainer}>
                            <View style={styles.badgeDot} />
                            <Typography variant="overline" style={styles.overline}>{t('communityJoin.badge')}</Typography>
                        </View>

                        <Typography variant="h2" style={[styles.title, { color: themeColors.textPrimary }]}>
                            {t('communityJoin.title')} <Text style={{ color: ACCENT_TEXT }}>{t('communityJoin.titleHighlight')}</Text>
                        </Typography>

                        <Typography variant="bodyLarge" style={styles.description}>
                            {t('communityJoin.description')}
                        </Typography>

                        <View style={styles.appButtons}>
                            <TouchableOpacity style={[styles.storeButton, { backgroundColor: isDark ? '#FFFFFF' : '#2B2730', borderColor: isDark ? '#FFFFFF' : '#2B2730' }]} activeOpacity={0.8}>
                                <Ionicons name="logo-apple" size={24} color={isDark ? '#2B2730' : 'white'} />
                                <View>
                                    <Typography style={[styles.storeSubtext, { color: isDark ? 'rgba(0,18,29,0.7)' : 'rgba(255,255,255,0.7)' }]}>{t('communityJoin.appStoreLabel')}</Typography>
                                    <Typography style={[styles.storeText, { color: isDark ? '#2B2730' : '#FFFFFF' }]}>{t('communityJoin.appStoreName')}</Typography>
                                </View>
                            </TouchableOpacity>

                            <TouchableOpacity style={[styles.storeButton, styles.storeButtonSecondary, { backgroundColor: themeColors.surfacePrimary, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,20,25,0.1)' }]} activeOpacity={0.8}>
                                <Ionicons name="logo-google-playstore" size={22} color={themeColors.textPrimary} />
                                <View>
                                    <Typography style={[styles.storeSubtext, { color: themeColors.textSecondary }]}>{t('communityJoin.playStoreLabel')}</Typography>
                                    <Typography style={[styles.storeText, { color: themeColors.textPrimary }]}>{t('communityJoin.playStoreName')}</Typography>
                                </View>
                            </TouchableOpacity>
                        </View>

                        <View style={styles.ratingRow}>
                            <View style={styles.stars}>
                                <Ionicons name="star" size={16} color={ACCENT_TEXT} />
                                <Ionicons name="star" size={16} color={ACCENT_TEXT} />
                                <Ionicons name="star" size={16} color={ACCENT_TEXT} />
                                <Ionicons name="star" size={16} color={ACCENT_TEXT} />
                                <Ionicons name="star" size={16} color={ACCENT_TEXT} />
                            </View>
                            <Typography style={styles.ratingText}>{t('communityJoin.rating')}</Typography>
                        </View>
                    </View>

                    {/* RIGHT COLUMN: VISUAL */}
                    <View style={[styles.visualColumn, isMedium && styles.visualColumnMedium]}>


                        {/* Phone Mockup */}
                        <View style={styles.phoneFrame}>
                            <View style={styles.notch} />
                            <View style={styles.sideBtnLeft} />
                            <View style={styles.sideBtnRight} />

                            <LinearGradient
                                colors={['#FF8C42', '#E98245']}
                                style={styles.screenContent}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                            >
                                <View style={styles.screenInner}>
                                    {/* Orange gradient backing — the transparent dark-theme
                                        asset (light wordmark) is the only one that works here. */}
                                    <BrandLogo variant="dark" height={60} />
                                </View>

                                <View style={styles.homeIndicator} />
                            </LinearGradient>
                        </View>

                        {/* Background Glow */}
                        <View style={styles.glow} />
                    </View>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        width: '100%',
        backgroundColor: '#FFFFFF',
        paddingBottom: 100,
    },
    container: {
        width: '100%',
        maxWidth: 1200,
        alignSelf: 'center',
        paddingHorizontal: 24,
    },
    content: {
        backgroundColor: '#FFFFFF', // Seamless
        borderRadius: 40,
        gap: 60,
        position: 'relative',
    },
    contentMedium: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 40,
    },
    textColumn: {
        gap: 32,
        zIndex: 2,
    },
    textColumnMedium: {
        flex: 1,
        maxWidth: 500,
    },
    badgeContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 8,
    },
    badgeDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#E98245',
    },
    overline: {
        color: ACCENT_TEXT,
        letterSpacing: 2,
        fontWeight: fontWeights.bold,
        fontSize: 13,
    },
    title: {
        fontSize: 48,
        lineHeight: 52,
        color: '#2B2730',
        letterSpacing: -1.5,
        fontWeight: fontWeights.bold,
    },
    description: {
        opacity: 0.6,
        lineHeight: 28,
        fontSize: 18,
    },
    appButtons: {
        flexDirection: 'row',
        gap: 16,
        flexWrap: 'wrap',
        marginTop: 16,
    },
    storeButton: {
        backgroundColor: '#2B2730',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        paddingHorizontal: 24,
        paddingVertical: 12, // More compact
        borderRadius: 12,
        minWidth: 170,
        borderWidth: 1,
        borderColor: '#2B2730',
    },
    storeButtonSecondary: {
        backgroundColor: '#FFFFFF',
        borderColor: 'rgba(15,20,25,0.1)',
    },
    storeSubtext: {
        fontSize: 13,
        fontWeight: fontWeights.bold,
        color: 'rgba(255,255,255,0.7)',
        letterSpacing: 0.5,
    },
    storeText: {
        fontSize: 16,
        fontWeight: fontWeights.bold,
        color: '#FFFFFF',
        letterSpacing: -0.3,
    },
    ratingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginTop: 16,
    },
    stars: {
        flexDirection: 'row',
        gap: 2,
    },
    ratingText: {
        fontSize: 13,
        fontWeight: '600',
        opacity: 0.5,
    },

    // VISUAL
    visualColumn: {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        height: 500,
        marginTop: 40,
    },
    visualColumnMedium: {
        flex: 1,
        height: 600,
        alignItems: 'flex-end',
        justifyContent: 'center',
        marginTop: 0,
        paddingRight: 60, // Increased for breathing room
    },



    // Phone
    phoneFrame: {
        width: 280,
        height: 560,
        backgroundColor: '#1C1C1E', // True black/dark grey hardware
        borderRadius: 48,
        borderWidth: 8,
        borderColor: '#252525',
        position: 'relative',
        zIndex: 10,
        overflow: 'hidden',
        transform: [
            { rotate: '-3deg' },
        ],
        ...Platform.select({
            web: {
                boxShadow: '0 50px 100px -20px rgba(0,0,0,0.3)',
            },
            default: {
                elevation: 20,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 20 },
                shadowOpacity: 0.4,
                shadowRadius: 40,
            }
        }),
    } as any,
    notch: {
        position: 'absolute',
        top: 0,
        alignSelf: 'center',
        width: 120,
        height: 30,
        backgroundColor: '#1C1C1E',
        borderBottomLeftRadius: 18,
        borderBottomRightRadius: 18,
        zIndex: 30,
    },
    sideBtnLeft: {
        position: 'absolute',
        left: -10,
        top: 100,
        width: 4,
        height: 40,
        backgroundColor: '#252525',
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
    },
    sideBtnRight: {
        position: 'absolute',
        right: -10,
        top: 120,
        width: 4,
        height: 80,
        backgroundColor: '#252525',
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
    },
    screenContent: {
        flex: 1,
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    screenInner: {
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    homeIndicator: {
        position: 'absolute',
        bottom: 8,
        width: 100,
        height: 4,
        borderRadius: 2,
        backgroundColor: 'white',
        alignSelf: 'center',
    },
    glow: {
        position: 'absolute',
        width: 400,
        height: 400,
        borderRadius: 200,
        backgroundColor: '#E98245',
        opacity: 0.1,
        zIndex: 0,
        filter: 'blur(100px)',
        top: '20%',
        right: -50,
    } as any,
});
